import { useQuery } from '@tanstack/react-query'
import type { Query } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import { MyAgencyActivationStateDocument } from '@/gql/graphql'
import type { ActivationStageState, AgreementStepReason } from '@/gql/graphql'

export type AgencyActivationStatus = 'PENDING' | 'APPROVED' | 'DECLINED'

/**
 * ADG-T02 — one OBSERVED signature on the agreement step's agreement, naming which side signed.
 * `role` is contract-svc's recipient-role literal (`CLIENT` = this org, `VENDOR` = the platform) —
 * never a persona. See `AgencyActivationStage.signatures` for the null/[] distinction this exists to
 * preserve.
 */
export interface AgreementSignatureHalf {
  role: string
  signedAt?: string | null
}

/** Same shape as the client ActivationStageItem — BE reuses the type (T07 handover). */
export interface AgencyActivationStage {
  key: string
  label: string
  state: ActivationStageState
  primaryAction?: string | null
  // The drafted contract id, populated iff state === NEEDED. Navigate CTA to /agreements/$actionId.
  actionId?: string | null
  /**
   * ADG-T02 — signatures OBSERVED on this step's agreement.
   *   null  — this step has no agreement (every machine stage). NOT APPLICABLE.
   *   []    — this IS the agreement step and no half has been observed yet.
   *   [...] — the halves observed so far.
   * Kept distinct on purpose (BE's own docblock, `client-activation-state.model.ts`) — do not treat
   * null and [] as the same fact. PRESENCE IS EVIDENCE, ABSENCE IS NOT: a present half licenses a
   * positive claim ("the other party has already signed"); an absent half licenses nothing — never
   * a negative ("has not signed"), a count, or a progress rail. A lost event degrades to silence.
   */
  signatures?: AgreementSignatureHalf[] | null
  /**
   * Why the agreement step is held (BE `ActivationStageItem.reason`, README §5b) — non-null on the AGREEMENT step only,
   * always null on machine stages. Render held copy from this, never from `state` and never from the absence of a NEEDED
   * stage. `ADVANCING` is client-only (never present on an agency board).
   */
  reason?: AgreementStepReason | null
}

export interface AgencyActivationState {
  status: AgencyActivationStatus
  // Nullable from BE at runtime — do not coerce to a default (T0024; be pinned this with a test).
  // Schema declares currentStage as Int! but the field can be null before any stage begins.
  currentStage: number | null
  submittedAt: string | null
  stages: AgencyActivationStage[]
}

export const myAgencyActivationStateKey = () => ['agency-activation', 'myState'] as const

/**
 * ADR-015 (rule 4) — how often to refetch the AGENCY activation query. The agency board legitimately rests in
 * "waiting on the platform" (its `review` stage is PLATFORM-actor, so no stage is NEEDED by design); while the
 * agreement step carries a `reason` and no stage is NEEDED, poll every 60 s so a hold resolves without a reload.
 * No interval while a stage is NEEDED or once the status leaves PENDING. No client-side timeout.
 */
export function agencyHeldPollIntervalMs(state: AgencyActivationState | undefined): number | false {
  if (!state || state.status !== 'PENDING') return false
  if (state.stages.some((s) => s.state === 'NEEDED')) return false
  return state.stages.find((s) => s.key === 'agreement')?.reason != null ? 60_000 : false
}

export function useMyAgencyActivationState(opts?: {
  enabled?: boolean
  refetchInterval?: number | false | ((query: Query<AgencyActivationState>) => number | false)
}) {
  return useQuery<AgencyActivationState>({
    queryKey: myAgencyActivationStateKey(),
    queryFn: async () => {
      const data = await fetchGql(MyAgencyActivationStateDocument)
      return data.myAgencyActivationState as AgencyActivationState
    },
    enabled: opts?.enabled ?? true,
    refetchInterval: opts?.refetchInterval ?? false,
    staleTime: 30_000,
    gcTime: 60_000,
  })
}
